import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { Pagesection, Tabs, TabsPanel } from '@ktag/styleguide/dist/react';

import ApiContext from 'api/ApiContext';
import { PollDetails, Statistics as StatisticsType } from 'api/api.types';
import CandidatePortraits, {
  FormattedCandidate,
} from 'lib/CandidatePortraits/CandidatePortraits';
import PortraitCloud from 'lib/PortraitCloud/PortraitCloud';
import Statistics from 'lib/Statistics/Statistics';
import useGetElectedCandidatesCanton from '../utils/useGetCandidatesCanton';
import { FormattedList } from '../utils/getFormattedCandidates';
import BiProportionalElectedMunis from './BiProportionalElectedMunis';
import ProportionalElectedTable from './ProportionalElectedTable';

type TabOptionLevel1 = 'canton' | 'districts';
type TabOptionLevel2 = 'portraits' | 'table' | 'statistics';

interface Props {
  candidates: FormattedCandidate[];
  isBiProportional: boolean;
  pollId: string;
  currentYear: number;
  previousElectionResults: PollDetails;
  statistics: StatisticsType;
  formattedParties: FormattedList[];
}

const ProportionalElected = ({
  candidates,
  isBiProportional,
  pollId,
  currentYear,
  previousElectionResults,
  statistics,
  formattedParties,
}: Props) => {
  const [level1ActiveTab, setLevel1ActiveTab] = useState<TabOptionLevel1>('canton');
  const [level2ActiveTab, setLevel2ActiveTab] = useState<TabOptionLevel2>('portraits');
  const { getAggregatingDomains } = useContext(ApiContext);
  const { t } = useTranslation('wap');

  useEffect(() => {
    if (level1ActiveTab === 'districts') {
      setLevel2ActiveTab('portraits');
    }
  }, [level1ActiveTab]);

  const { data: aggregatingDomainsData } = useQuery(['poll', pollId], () =>
    getAggregatingDomains(pollId),
  );

  const electedCandidatesCanton = useGetElectedCandidatesCanton(aggregatingDomainsData, {
    isBiProportional,
    showOnlyElected: true,
    addEmptySeats: true,
  });

  // if a list receives more seats than they had candidates
  const openSeats = electedCandidatesCanton?.filter(
    (candidate) => !candidate.firstName && !candidate.lastName,
  ).length;

  const getActiveTabContent = () => {
    switch (level2ActiveTab) {
      case 'portraits':
        return isBiProportional ? (
          <Pagesection
            type="medium"
            additionalClassNames="proportionalelectiondetails__tabs"
          >
            <PortraitCloud electedCandidatesCanton={electedCandidatesCanton} />
          </Pagesection>
        ) : (
          <CandidatePortraits
            candidates={candidates.filter((candidate) => candidate.elected)}
          />
        );
      case 'table':
        return (
          <ProportionalElectedTable
            candidates={candidates}
            electedCandidatesCanton={electedCandidatesCanton}
            isBiProportional={isBiProportional}
          />
        );
      case 'statistics':
        return (
          <Statistics
            currentYear={currentYear}
            currentStatistics={statistics}
            previousYear={new Date(previousElectionResults?.info?.date).getFullYear()}
            previousStatistics={
              previousElectionResults?.results?.proportional_election_data?.statistics
                ?.elected_candidates
            }
            openSeats={openSeats}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      {isBiProportional && (
        <Pagesection>
          <Tabs ariaLabel={t('proportional_tablist_elected_level2')}>
            <TabsPanel
              label={t('canton')}
              onActive={() => setLevel1ActiveTab('canton')}
              id="canton"
            />
            <TabsPanel
              label={t('proportional_districts')}
              onActive={() => setLevel1ActiveTab('districts')}
              id="districts"
            />
          </Tabs>
        </Pagesection>
      )}
      {level1ActiveTab === 'canton' && (
        <>
          <Pagesection>
            <Tabs
              noBezel
              type="button"
              ariaLabel={t('proportional_tablist_elected_canton_level3')}
            >
              <TabsPanel
                label={t('persons')}
                icon="user"
                id="persons"
                onActive={() => setLevel2ActiveTab('portraits')}
              />
              <TabsPanel
                label={t('table')}
                icon="table"
                id="table"
                onActive={() => setLevel2ActiveTab('table')}
              />
              {statistics && (
                <TabsPanel
                  label={t('statistics')}
                  icon="male-female"
                  id="statistics"
                  additionalClassNames="proportionalelectiondetails__tab--stats"
                  onActive={() => setLevel2ActiveTab('statistics')}
                />
              )}
            </Tabs>
          </Pagesection>
          {getActiveTabContent()}
        </>
      )}
      {level1ActiveTab === 'districts' && aggregatingDomainsData && (
        <Pagesection additionalClassNames="proportionalelectiondetails__districts">
          <BiProportionalElectedMunis
            aggregatingDomainsData={aggregatingDomainsData}
            formattedParties={formattedParties}
          />
        </Pagesection>
      )}
    </>
  );
};

export default ProportionalElected;
